import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { InvoiceDialog } from './InvoiceDialog';

export const TONE = { Reading: 'bg-blue-100 text-blue-800', 'Needs review': 'bg-amber-100 text-amber-800', Approved: 'bg-green-100 text-green-800', Paid: 'bg-gray-900 text-[#d4af37]', Rejected: 'bg-red-100 text-red-800' };
export const errText = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(', ') : typeof d === 'string' ? d : ''; };
const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;

export const openInvoiceFile = async (inv) => {
  const r = await api.get(`/contractor-invoices/${inv.id}/file`, { responseType: 'blob' });
  window.open(URL.createObjectURL(r.data), '_blank');
};

const csv = (rows) => {
  const head = ['Contractor', 'Business', 'Invoice #', 'Invoice date', 'Period', 'Total', 'Status', 'Paid date', 'Method', 'Reference'];
  const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.contractorName, r.businessName, r.invoiceNumber, r.invoiceDate, `${r.periodStart || ''} - ${r.periodEnd || ''}`, (r.total || 0).toFixed(2), r.status, r.paidDate, r.paidMethod, r.paidReference].map(q).join(','));
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([[head.join(','), ...body].join('\n')], { type: 'text/csv' }));
  a.download = 'contractor_invoices.csv'; a.click();
};

export const ContractorInvoices = () => {
  const [rows, setRows] = useState([]);
  const [isOffice, setIsOffice] = useState(false);
  const [status, setStatus] = useState('All');
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/contractor-invoices', { params: { status } }); setRows(r.data.invoices); setIsOffice(r.data.isOffice); }
    catch { toast.error('Could not load invoices'); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { // refresh while an invoice is still being read
    if (!rows.some((r) => r.status === 'Reading')) return undefined;
    const t = setTimeout(load, 4000); return () => clearTimeout(t);
  }, [rows, load]);
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f);
      try { await api.post('/contractor-invoices', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${f.name} uploaded - reading it now`); }
      catch (e) { toast.error(errText(e) || `Could not upload ${f.name}`); }
    }
    setBusy(false); if (input.current) input.current.value = ''; load();
  };
  const sum = (s) => rows.filter((r) => r.status === s).reduce((t, r) => t + (r.total || 0), 0);
  return (
    <div className="space-y-4">
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]); }}
        className="border-2 border-dashed border-[#d4af37] rounded-xl p-6 bg-amber-50/40 flex flex-wrap items-center gap-4" data-testid="invoice-dropzone">
        <Upload className="w-8 h-8 text-[#b8860b]" />
        <div className="flex-1 min-w-[220px]"><p className="font-semibold">Drop invoices here or choose files</p><p className="text-sm text-gray-600">PDF or photo, up to 15 MB each. The contractor, dates, visits and amounts are filled in for you to check.</p></div>
        <input ref={input} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="invoice-file-input" />
        <button disabled={busy} onClick={() => input.current?.click()} className="px-4 py-2 rounded-lg bg-gray-900 text-[#d4af37] font-semibold disabled:opacity-50" data-testid="invoice-upload-btn">{busy ? 'Uploading…' : 'Upload invoice'}</button>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5" data-testid="invoice-status-filter">
          {['All', 'Reading', 'Needs review', 'Approved', 'Paid', 'Rejected'].map((s) => <option key={s}>{s}</option>)}</select>
        {isOffice && <span className="font-semibold" data-testid="invoice-totals">To review {money(sum('Needs review'))} · Approved, unpaid {money(sum('Approved'))} · Paid {money(sum('Paid'))}</span>}
        {isOffice && <button onClick={() => csv(rows)} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 border rounded bg-white" data-testid="invoice-export"><Download className="w-4 h-4" />Export for payroll (CSV)</button>}
      </div>
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-[#d4af37] text-left"><tr>{['Uploaded', 'Contractor', 'Invoice #', 'Period', 'Total', 'Status', ''].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`invoice-row-${r.id}`}>
                <td className="px-2 py-1.5">{new Date(r.uploadedAt).toLocaleDateString()}<div className="text-xs text-gray-500">{r.uploadedBy}</div></td>
                <td className="px-2 py-1.5">{r.contractorName || <span className="text-gray-400">—</span>}<div className="text-xs text-gray-500">{r.businessName}</div></td>
                <td className="px-2 py-1.5">{r.invoiceNumber || '—'}</td>
                <td className="px-2 py-1.5">{r.periodStart || '—'}{r.periodEnd ? ` to ${r.periodEnd}` : ''}</td>
                <td className="px-2 py-1.5 font-semibold">{money(r.total)}</td>
                <td className="px-2 py-1.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TONE[r.status] || ''}`} data-testid={`invoice-status-${r.id}`}>{r.status}</span></td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <button onClick={() => openInvoiceFile(r)} className="p-1" aria-label="View original" data-testid={`invoice-view-${r.id}`}><Eye className="w-4 h-4" /></button>
                  <button disabled={r.status === 'Reading'} onClick={() => setOpen(r.id)} className="ml-1 px-2 py-1 rounded border text-xs disabled:opacity-40" data-testid={`invoice-open-${r.id}`}>{r.status === 'Reading' ? 'Reading…' : 'Open'}</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-2 py-4 text-gray-500" data-testid="invoice-empty">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <InvoiceDialog id={open} isOffice={isOffice} onClose={() => setOpen(null)} onDone={load} />}
    </div>
  );
};
